import AxiosService from './axiosService'
import authHeader from './authHeader'
import AppEnvService from '@core/services/utils/appEnvService'

/**
 * Map axis/service name to remote folder name
 */
const AXIS_TO_REMOTE = {
  ACE: 'ace',
  APP: 'app',
  MODULAR: 'modular',
  RMS: 'rms',
  TAL: 'tal',
  TEV: 'tev',
  PRONOVA: 'pronova',
  SHELL: 'shell',
  AIA: 'aia'
}

class RouterService {
  constructor() {
    this.router = null
    this.routes = null
    this.serviceName = null
    this.menuStore = null
    this.pageModules = {}
    this.layoutModules = {}
    this.initialized = false
  }

  /**
   * Initialize the router service with glob-imported page modules
   * @param {Object} config - Configuration object
   * @param {Object} config.router - Vue Router instance
   * @param {Array} config.routes - Base routes
   * @param {string} config.serviceName - Service name (e.g., 'SHELL')
   * @param {Object} config.pageModules - All page modules from import.meta.glob
   * @param {Object} config.layoutModules - All layout modules from import.meta.glob
   * @param {Object} config.menuStore - Menu store instance
   */
  init({ router, routes, serviceName, pageModules, layoutModules, menuStore }) {
    this.router = router
    this.routes = routes
    this.serviceName = serviceName
    this.menuStore = menuStore

    // Store all page modules (from shell's import.meta.glob)
    if (pageModules) {
      this.pageModules = pageModules
      console.log(`[RouterService] Pages registered: ${Object.keys(pageModules).length}`)
    }

    // Store all layout modules
    if (layoutModules) {
      this.layoutModules = layoutModules
      console.log(`[RouterService] Layouts registered: ${Object.keys(layoutModules).length}`)
      console.log('[RouterService] Layout paths:', Object.keys(layoutModules))
    }

    this.initialized = true
    console.log('[RouterService] Initialized with serviceName:', serviceName)
  }

  /**
   * Resolve a page component by axis (service name) and component name
   * among the physical files collected by import.meta.glob.
   *
   * @param {string} axis - The service/axis name (e.g., 'RMS', 'TEV', 'aia')
   * @param {string} componentName - The component name (e.g., 'rmsKanban', 'aiaCertificationPage')
   * @returns {Function|null} - Loader function or null if not found
   */
  resolvePageComponent(axis, componentName) {

    for (const [path, loader] of Object.entries(this.pageModules)) {
      if (path.endsWith(`/${componentName}.vue`)) {
        console.log(`[RouterService] Found ${componentName} at ${path}`)
        return loader
      }
    }

    console.warn(`[RouterService] Component not found: ${axis}/${componentName}`)
    return null
  }

  /**
   * Get APP_CODE from AppEnvService (loaded from DB via AppSetting)
   */
  getAppCode() {
    return AppEnvService.getEnv('APP_CODE') || AppEnvService.getAppCode() || 'tal'
  }

  /**
   * Load menu from backend, populate store, and generate routes.
   */
  async loadMenu() {
    try {
      const { data } = await AxiosService.post('/appMenu/list', { service: this.serviceName }, { headers: authHeader() })
      if (data?.menus) {
        // Populate the menuStore so TreeMenu can display menus
        if (this.menuStore) {
          this.menuStore.load(data.menus)
          console.log(`[RouterService] Menu store populated with ${data.menus.length} items`)
        }

        // Generate and apply routes
        const dynamicRoutes = this.generateRoutes(data.menus)
        this.applyRoutes(dynamicRoutes)

        // Store routes reference in menuStore
        if (this.menuStore) {
          this.menuStore.setRoutes(this.routes || [], dynamicRoutes)
        }

        console.log(`[RouterService] ${dynamicRoutes.length} routes generated`)
      }
    } catch (error) {
      console.error('[RouterService] Failed to load menu:', error.message)
    }
  }

  /**
   * Refresh routes - call loadMenu again
   */
  refreshRoutes() {
    return this.loadMenu()
  }

  /**
   * Generate Vue Router routes from menu items
   */
  generateRoutes(menuItems, routes = []) {
    for (const item of menuItems) {
      if (item.route) {
        const { axis, component, path, name } = item.route
        const loader = this.resolvePageComponent(axis, component)
        if (loader) {
          routes.push({ path, name, component: loader })
        }
      }
      if (item.nodes?.length) {
        this.generateRoutes(item.nodes, routes)
      }
    }
    return routes
  }

  /**
   * Apply routes to Vue Router
   * Adds dynamic routes as children to the existing 'main-layout' route
   */
  applyRoutes(dynamicRoutes) {
    if (!this.router) {
      console.error('[RouterService] Router not initialized')
      return
    }

    // Add each dynamic route as a child of the 'main-layout' route
    for (const route of dynamicRoutes) {
      this.router.addRoute('main-layout', route)
    }

    console.log(`[RouterService] Applied ${dynamicRoutes.length} routes under main-layout`)

    // If the user is sitting on the catch-all (Loading page) — typically because
    // they refreshed on a dynamic route like /ace/meeting that wasn't registered
    // yet — try to resolve their URL against the freshly added routes. If a real
    // route now matches, navigate there. Otherwise, show ErrorNotFound.
    const currentRoute = this.router.currentRoute.value
    const isCatchAll = currentRoute.matched.some(m => m.path.includes('catchAll'))

    if (isCatchAll) {
      const currentPath = currentRoute.fullPath
      const resolved = this.router.resolve(currentPath)
      const stillUnknown = !resolved.name || resolved.name === 'not-found'
      if (!stillUnknown) {
        console.log(`[RouterService] Re-navigating to: ${currentPath}`)
        this.router.replace(currentPath)
      } else if (currentPath !== '/') {
        console.log(`[RouterService] Route ${currentPath} truly unknown — redirecting to error-not-found`)
        this.router.replace({ name: 'error-not-found' })
      }
    }
  }

  getRemoteRouter() {
    return this.router
  }
}

export default new RouterService()
